import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../lib/api';
import { isAdmin } from '../lib/utils';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const sessionId = localStorage.getItem('sessionId');
        if (sessionId) {
            loadSession(sessionId);
        } else {
            setLoading(false);
        }
    }, []);

    const loadSession = async (sessionId) => {
        try {
            const response = await authApi.getSession(sessionId);
            setUser(response.data);
        } catch (error) {
            console.error('Session load failed:', error);
            localStorage.removeItem('sessionId');
        } finally {
            setLoading(false);
        }
    };

    const login = async (sessionData) => {
        // For Discord OAuth, we receive session data directly from the callback
        const userData = {
            id: sessionData.session_id,
            discord_id: sessionData.discord_id,
            username: sessionData.username,
            avatar: sessionData.avatar,
            roles: sessionData.roles || [],
            is_admin: sessionData.is_admin || false,
        };
        
        localStorage.setItem('sessionId', sessionData.session_id);
        setUser(userData);
        return userData;
    };

    const logout = async () => {
        const sessionId = localStorage.getItem('sessionId');
        if (sessionId) {
            try {
                await authApi.logout(sessionId);
            } catch (error) {
                console.error('Logout API failed:', error);
            }
        }
        localStorage.removeItem('sessionId');
        setUser(null);
    };

    const value = {
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!user,
        isAdmin: user ? (user.is_admin || isAdmin(user.roles)) : false,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
